/**
 * Зеркало docs/API.md. Единственный источник типов ответов API.
 *
 * Все денежные величины — строки: JSON-число теряет точность, а Decimal на
 * бэкенде специально выбран, чтобы её не терять. Форматирование — только через
 * shared/lib/money.ts, арифметика на фронте запрещена (кроме визуальных эффектов).
 */

export type Money = string;
export type Iso = string;

export type Locale = 'ru' | 'en' | 'es' | 'tr' | 'de' | 'fr';
export const LOCALES: Locale[] = ['ru', 'en', 'es', 'tr', 'de', 'fr'];

export type UserRole = 'client' | 'admin' | 'owner';
export type RoundStatus = 'entry_open' | 'entry_closed' | 'closed';
export type RoundMode = 'core_only' | 'client_round';
// RoundTier removed: now using numeric level (1, 2, 3...)
export type ResultInputMode = 'net_profit' | 'total_amount';
export type WithdrawalStatus = 'pending' | 'fulfilled' | 'rejected';
/**
 * Статус заявки на пополнение (зеркало DepositStatus бэкенда).
 * `confirming` — транзакция найдена, ждём подтверждений сети;
 * `manual_review` — автопроверка не смогла решить, разбирает управляющий.
 */
export type DepositStatus = 'pending' | 'confirming' | 'credited' | 'manual_review' | 'rejected';
export type PayoutNetwork = 'bep20' | 'ton';
export type AchievementCategory = 'participation' | 'profit' | 'loyalty' | 'referral' | 'consistency' | 'capital';
export type JoinBlockedReason =
  | 'limit_not_set'
  | 'limit_exceeded'
  | 'already_joined'
  | 'entry_closed'
  | 'level_not_approved'
  | 'cannot_enter_lower_ring'
  | 'cannot_rejoin'
  /** Свободной суммы (капитал + прибыль) не хватает на взнос. */
  | 'insufficient_funds';

export interface ApiErrorBody {
  error: { code: string; message: string; context?: Record<string, unknown> };
}

export interface Paged<T> {
  items: T[];
  next_cursor: string | null;
  total: number;
}

export interface SeriesPoint {
  t: Iso;
  v: Money;
}

export interface ExternalFundsPoint {
  date: string; // ISO date YYYY-MM-DD
  deposits: Money;
  adjustments?: Money;
  withdrawals: Money;
  earnings: Money;
}

/* -------------------------------------------------------------------------- */
/* Auth                                                                        */
/* -------------------------------------------------------------------------- */

export interface TokenPair {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user: Me;
}

export interface TelegramWidgetPayload {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

/* -------------------------------------------------------------------------- */
/* Public                                                                      */
/* -------------------------------------------------------------------------- */

export interface MarketConfig {
  tz: string;
  /** [["00:00","02:00"]] — ежедневные закрытые интервалы */
  daily_closed: [string, string][];
  /** 0=Mon .. 6=Sun */
  closed_weekdays: number[];
  is_open: boolean;
  next_change_at: Iso;
}

export interface PublicConfig {
  currency: string;
  locales: Locale[];
  bot_username: string;
  channel_url: string | null;
  chat_url: string | null;
  support_url: string | null;
  games_enabled: boolean;
  min_withdrawal: Money;
  market: MarketConfig;
  server_time: Iso;
}

export interface PublicActiveRing {
  seq: number;
  opened_at: Iso;
  status: RoundStatus;
}

export interface PublicStats {
  rings_total: number;
  profit_total: Money;
  participants_total: number;
  core_display_value: Money;
  active_ring: PublicActiveRing | null;
}

export interface FaqItem {
  id: number;
  question: string;
  answer: string;
  category: string | null;
}

/* -------------------------------------------------------------------------- */
/* Client: профиль                                                             */
/* -------------------------------------------------------------------------- */

export interface Me {
  id: number;
  display_name: string;
  tg_username: string | null;
  role: UserRole;
  locale: Locale;
  full_name: string | null;
  email: string | null;
  payout_address_bep20: string | null;
  payout_address_ton: string | null;
  payout_preferred: PayoutNetwork | null;
  /** null => участие невозможно, пока клиент сам не задаст лимит */
  contribution_limit: Money | null;
  capital: Money;
  accrued_profit: Money;
  reserved_profit: Money;
  total_withdrawn: Money;
  /** Сумма для автовхода: null = выключен, значение = максимум ставки */
  auto_enter_amount: Money | null;
  /** Автовход использует общий contribution_limit. */
  auto_enter_enabled: boolean;
  /** Текущий максимальный уровень доступа (1, 2, 3...) */
  current_level: number | null;
  /** Запрошенный уровень (ждёт одобрения админа) */
  pending_level: number | null;
  /** Когда запрошен уровень: профиль показывает дату в статусе запроса */
  pending_level_requested_at: Iso | null;
  notify_digest: boolean;
  notify_events: boolean;
  /** Задан ли пин-код. Сам пин на клиент не приходит никогда. */
  pin_set: boolean;
  /** Когда пользователь принял предупреждение о рисках. null = не принял. */
  risk_accepted_at: Iso | null;
  referral_code: string | null;
  referrals_count: number;
  rings_count: number;
  achievements_unlocked: number;
  created_at: Iso;
}

export interface ParticipationSettingsPatch {
  contribution_limit: Money;
  auto_enter_enabled: boolean;
}

export interface ProfilePatch {
  full_name?: string | null;
  email?: string | null;
  payout_address_bep20?: string | null;
  payout_address_ton?: string | null;
  payout_preferred?: PayoutNetwork | null;
}

export interface PaymentHistoryItem {
  id: number;
  type: 'deposit' | 'withdrawal' | 'earning';
  amount: Money;
  timestamp: Iso;
  status: string | null;
  round_id: number | null;
}

export interface PaymentHistoryPage extends Paged<PaymentHistoryItem> {
  has_more: boolean;
}

export interface DepositInfo {
  currency: string;
  bep20: string | null;
  ton: string | null;
  instructions: string | null;
}

/**
 * Заявка на пополнение: клиент присылает хэш перевода, система проверяет его
 * в блокчейне и зачисляет сумму в capital.
 *
 * `claimed_amount` — то, что заявил клиент, справочно. Зачисляется
 * `credited_amount`: источник истины — сумма в самой транзакции.
 */
export interface DepositClaim {
  id: number;
  network: PayoutNetwork;
  tx_hash: string;
  status: DepositStatus;
  claimed_amount: Money | null;
  /** Найдено в сети при последней проверке. */
  onchain_amount: Money | null;
  credited_amount: Money | null;
  confirmations: number;
  /** Порог автозачисления: показываем прогресс «7 из 12». */
  required_confirmations: number | null;
  admin_note: string | null;
  created_at: Iso;
  credited_at: Iso | null;
}

export interface DepositClaimInput {
  tx_hash: string;
  network: PayoutNetwork;
  /** Необязательна: на зачисление не влияет, помогает при ручном разборе. */
  claimed_amount?: string | null;
}

/** То же плюс поля для разбора админом. */
export interface AdminDepositClaim extends DepositClaim {
  client_id: number;
  client_name: string | null;
  client_full_name: string | null;
  client_email: string | null;
  from_address: string | null;
  to_address: string | null;
  block_number: number | null;
  check_attempts: number;
  checked_at: Iso | null;
  /** Причина ухода в ручной разбор или последняя ошибка опроса сети. */
  last_error: string | null;
  processed_by_id: number | null;
}

export interface AdminDepositList {
  items: AdminDepositClaim[];
  next_cursor: string | null;
  /** Сколько заявок ждёт ручного разбора — размер очереди работы. */
  review_count: number;
  /** Сколько ещё проверяется автоматически. */
  open_count: number;
}

/* -------------------------------------------------------------------------- */
/* Client: кольца                                                              */
/* -------------------------------------------------------------------------- */

export interface MyParticipation {
  contribution: Money;
  joined_at: Iso;
  auto_joined: boolean;
}

/**
 * Параметры детерминированной клиентской анимации (D-10).
 * `scale` огрублена до порядка величины — по ней нельзя восстановить
 * target_profit_hint управляющего.
 */
export interface LiveParams {
  seed: number;
  scale: Money;
  active_seconds: number;
  duration_hours: number;
  start_ratio: number;
  speed_factor: number;
}

export interface ActiveRound {
  id: number;
  seq: number;
  mode: RoundMode;
  level: number;
  status: Exclude<RoundStatus, 'closed'>;
  contribution_amount: Money | null;
  contribution_min: Money | null;
  contribution_max: Money | null;
  opened_at: Iso;
  entry_closed_at: Iso | null;
  participants_count: number;
  live: LiveParams;
  my: {
    can_join: boolean;
    blocked_reason: JoinBlockedReason | null;
    participation: MyParticipation | null;
  };
}

export interface MyRing {
  round_id: number;
  seq: number;
  status: RoundStatus;
  mode: 'regular' | 'core_only';
  level: number;
  /** 0..1 — процессная целостность кольца, одинакова для всех наблюдателей (D-04) */
  fullness: number | null;
  contribution: Money;
  my_capital: Money | null;
  total_amount: Money | null;
  profit_share: Money | null;
  return_rate: number | null;
  joined_at: Iso;
  withdrawn_at: Iso | null;
  opened_at: Iso;
  closed_at: Iso | null;
  duration_sec: number | null;
}

/* -------------------------------------------------------------------------- */
/* Client: статистика                                                          */
/* -------------------------------------------------------------------------- */

export interface MyStats {
  capital: Money;
  accrued_profit: Money;
  reserved_profit: Money;
  total_withdrawn: Money;
  rings_total: number;
  rings_full: number;
  rings_cut: number;
  best_return_rate: number | null;
  current_streak: number;
}

export interface LeaderboardEntry {
  rank: number;
  display_name: string;
  is_me: boolean;
  score: Money;
  rings_count: number;
  series: SeriesPoint[];
}

export interface GlobalStats {
  rings_total: number;
  profit_total: Money;
  participants_total: number;
  money_in_circulation: Money;
  core_display_value: Money;
  avg_fullness: number | null;
  leaderboard: LeaderboardEntry[];
  external_funds_series: SeriesPoint[];
}

/* -------------------------------------------------------------------------- */
/* Client: выводы, ачивки, рефералы                                            */
/* -------------------------------------------------------------------------- */

export interface Withdrawal {
  id: number;
  amount: Money;
  status: WithdrawalStatus;
  network: PayoutNetwork;
  address: string;
  admin_note: string | null;
  tx_hash: string | null;
  requested_at: Iso;
  fulfilled_at: Iso | null;
  rejected_at: Iso | null;
}

export interface AchievementView {
  code: string;
  category: AchievementCategory;
  tier: number;
  icon: string;
  threshold: Money;
  progress: Money;
  unlocked_at: Iso | null;
}

export interface AchievementsResponse {
  /** All active tiers; display limits never truncate a family's ladder. */
  items: AchievementView[];
  unlocked: number;
  total: number;
  /** Initially visible families. The user can expand the full list. */
  display_limit: number;
}

export interface ReferralStats {
  code: string;
  link: string;
  invited_count: number;
  invited_active: number;
  invited_capital_total: Money;
  total_closed_rings: number;
  series: SeriesPoint[];
  invited: {
    id: number;
    display_name: string;
    joined_at: Iso;
    level: number;
    rings_count: number;
    capital: Money;
  }[];
  available_levels: number[];
  next_cursor: string | null;
}

/* -------------------------------------------------------------------------- */
/* Admin                                                                       */
/* -------------------------------------------------------------------------- */

export interface AdminClient {
  id: number;
  display_name: string;
  tg_user_id: number | null;
  tg_username: string | null;
  role: UserRole;
  contribution_limit: Money | null;
  capital: Money;
  accrued_profit: Money;
  reserved_profit: Money;
  total_withdrawn: Money;
  auto_enter_amount: Money | null;
  current_level: number | null;
  pending_level: number | null;
  rings_count: number;
  referred_by: string | null;
  referred_by_id: number | null;
  referral_code: string | null;
  created_at: Iso;
  deleted_at: Iso | null;
}

/** GET /admin/clients/{id} — карточка + история участий */
export interface AdminClientDetail extends AdminClient {
  full_name: string | null;
  email: string | null;
  payout_address_bep20: string | null;
  payout_address_ton: string | null;
  locale: string;
  role: 'client' | 'admin' | 'owner';
  pin_set: boolean;
  rings: MyRing[];
}

export interface CoreTopupView {
  id: number;
  amount: Money;
  reason:
    | 'manual_topup'
    | 'client_deletion'
    | 'round_remainder'
    | 'round_core_fee'
    | 'core_only_result'
    | 'round_loss';
  balance_after: Money;
  note: string | null;
  source_client_name: string | null;
  round_seq: number | null;
  created_at: Iso;
}

export interface CoreState {
  /** Бухгалтерский факт. МОЖЕТ быть отрицательным (D-03) — показывать как есть. */
  balance: Money;
  display_value: Money;
  updated_at: Iso;
  topups: Paged<CoreTopupView>;
}

export interface AdminWithdrawal extends Withdrawal {
  client_id: number;
  client_name: string;
  client_full_name: string | null;
  client_email: string | null;
  client_tg_username: string | null;
  processed_by: string | null;
}

/**
 * Страница заявок с итогами.
 *
 * `total_*` считаются по текущим фильтрам, `pending_*` — всегда по всей
 * очереди: объём ручной работы виден и на вкладке обработанных.
 */
export interface AdminWithdrawalList {
  items: AdminWithdrawal[];
  next_cursor: string | null;
  total_count: number;
  total_amount: Money;
  pending_count: number;
  pending_amount: Money;
}

/** Админский вид раунда — единственное место, где отдаётся target_profit_hint. */
export interface AdminRound {
  id: number;
  seq: number;
  mode: RoundMode;
  level: number;
  status: RoundStatus;
  contribution_amount: Money | null;
  contribution_min: Money | null;
  contribution_max: Money | null;
  target_profit_hint: Money;
  core_fee_pct: string;
  admin_fee_pct: string;
  opened_at: Iso;
  entry_closed_at: Iso | null;
  closed_at: Iso | null;
  real_result: Money | null;
  result_input_mode: ResultInputMode | null;
  fullness: number | null;
  core_amount: Money | null;
  admin_amount: Money | null;
  distributed_amount: Money | null;
  per_participant: Money | null;
  participants_count: number;
  created_by: string;
  closed_by: string | null;
}

export interface AdminActiveRound extends AdminRound {
  live: LiveParams;
  min_client_limit: Money | null;
  participants: {
    client_id: number;
    display_name: string;
    contribution: Money;
    joined_at: Iso;
    withdrawn_at: Iso | null;
    auto_joined: boolean;
  }[];
  active_participants_count: number;
  total_contribution: Money;
}

export interface ClosePreview {
  real_result: Money;
  input_mode: ResultInputMode;
  core_amount: Money;
  admin_amount: Money;
  distributed_amount: Money;
  per_participant: Money;
  remainder_to_core: Money;
  participants_count: number;
  fullness: number;
  is_loss: boolean;
}

/** Последние параметры уровня — предзаполнение формы следующего раунда. */
export interface RingTemplate {
  level: number;
  mode: RoundMode;
  contribution_amount: Money | null;
  target_profit_hint: Money;
  duration_hours: number;
}

/** Состав следующего раунда: меньше `min_levels` колец запустить нельзя. */
export interface RingSet {
  min_levels: number;
  max_levels: number;
  rings: RingTemplate[];
}

/** Historical profit actually credited to participants, grouped by ring level. */
export interface AdminProfitShares {
  total_profit: Money;
  rings: {
    level: number;
    profit: Money;
    /** Hundredths of a percent; adds up to 10,000 when total_profit is positive. */
    share_bps: number;
  }[];
}

export interface AdminDashboard {
  clients_total: number;
  clients_linked: number;
  deposits_total: Money;
  withdrawals_total: Money;
  money_in_circulation: Money;
  core_balance: Money;
  admin_earnings_total: Money;
  pending_withdrawals: { count: number; amount: Money };
  pending_deposits: { count: number; amount: Money };
  pending_level_requests: number;
  total_capital: Money;
  total_profit: Money;
  rings_total: number;
  active_round: AdminActiveRound | null;
  leaderboard: LeaderboardEntry[];
  external_funds_series: ExternalFundsPoint[];
}

export interface AuditEntry {
  id: number;
  actor_name: string | null;
  action: string;
  entity: string;
  entity_id: number | null;
  payload: Record<string, unknown> | null;
  created_at: Iso;
}

export interface BroadcastView {
  id: number;
  actor_name: string | null;
  text: Record<string, string> | { all: string };
  recipients_total: number;
  recipients_delivered: number;
  recipients_failed: number;
  created_at: Iso;
  finished_at: Iso | null;
}

export interface AdminFaqEntry {
  id: number;
  question: Partial<Record<Locale, string>>;
  answer: Partial<Record<Locale, string>>;
  category: string | null;
  sort_order: number;
  is_published: boolean;
}

export interface StaffMember {
  id: number;
  display_name: string;
  tg_username: string | null;
  role: UserRole;
  created_at: Iso;
}

export interface SettingEntry {
  key: string;
  value: unknown;
  description: string | null;
  updated_at: Iso;
  updated_by: string | null;
}

/* -------------------------------------------------------------------------- */
/* Рефералы                                                                   */
/* -------------------------------------------------------------------------- */

export interface ReferralLevel {
  count: number;
  external_funds: Money;
}

/** GET /client/referrals/me */
export interface MyReferrals {
  code: string;
  link: string;
  total_referrals: number;
  active_referrals: number;
  total_external_funds: Money;
  /** Ключ — номер уровня строкой ("1", "2", ...). */
  levels: Record<string, ReferralLevel>;
  series: SeriesPoint[];
}

export interface InvitedUser {
  display_name: string;
  joined_at: Iso;
  rings_count: number;
}

/** GET /client/referrals/list */
export interface ReferralList {
  invited: InvitedUser[];
  invited_count: number;
  invited_active: number;
  invited_capital_total: Money;
}

/** GET /client/referrals/tree — рекурсивный узел. */
export interface ReferralTreeNode {
  user_id: number;
  display_name: string;
  capital: Money;
  referrals_count: number;
  children: ReferralTreeNode[];
}

/** GET /admin/referrals/overview */
export interface AdminReferralsOverview {
  top_referrers: {
    user_id: number;
    display_name: string;
    referral_code: string;
    referrals_count: number;
    external_funds: Money;
  }[];
  total_stats: {
    total_referrers: number;
    total_referred_users: number;
    total_active_referred: number;
    total_external_funds: Money;
  };
}

/** GET /admin/referrals/user/{id}/stats */
export interface AdminReferralStats {
  user_id: number;
  display_name: string;
  referral_code: string | null;
  stats: {
    total_referrals: number;
    active_referrals: number;
    network_referrals: number;
    network_active_referrals: number;
    total_external_funds: Money;
    levels: Record<string, ReferralLevel & { active_count: number }>;
  };
}

/* -------------------------------------------------------------------------- */
/* Level Requests                                                              */
/* -------------------------------------------------------------------------- */

/** POST /client/me/request-level */
export interface RequestLevelPayload {
  requested_level: number;
}

/** GET /admin/level-requests */
export interface AdminLevelRequest {
  user_id: number;
  display_name: string;
  tg_username: string | null;
  current_level: number;
  requested_level: number;
  requested_at: Iso;
}

/** PATCH /admin/clients/{id}/level */
export interface AdminSetLevelPayload {
  level: number;
}
